import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton
} from '@mui/material'
import { ArrowDownward, ArrowUpward, ExpandLess, ExpandMore } from '@mui/icons-material'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState, useRef } from 'react'
import { GlobalContext } from '../context/GlobalContext'
import { useLocation } from 'react-router-dom'
import useApi from '../hooks/useApi'
import EnquiryItem from './EnquiryItem'
import useLockStore from '../stores/useLockStore'
import useDataStatusStore from '../stores/useDataStatusStore'
import useLocalStorage from '../hooks/useLocalStorage'
import { sortEnquiries } from '../lib/util'

const LockDebug = () => {
  const locks = useLockStore((state) => state.locks)
  // return <div>{JSON.stringify(locks)}</div>
}

const defaultStatusFilters = {
  'FactorUnread': true,
  'Garage Confirmed': true,
  'Waiting': true,
  'Order': true,
  'Ordered': false,
  'Quoted': false,
  'Cancelled': false
}

const statusLabels = {
  'FactorUnread': 'Unread Messages',
  'Garage Confirmed': 'Garage Confirmed',
  'Waiting': 'To Quote',
  'Order': 'Direct Order',
  'Ordered': 'Ordered',
  'Quoted': 'Quoted',
  'Cancelled': 'Cancelled'
}

const Enquiries = () => {
  const { apir, token, username } = useContext(GlobalContext)
  const setDataStatus = useDataStatusStore((state) => state.setStatus)
  const resetDataStatus = useDataStatusStore((state) => state.reset)
  const [startDate, setStartDate] = useState(dayjs().startOf('week').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().endOf('week').format('YYYY-MM-DD'))
  const [searchTerm, setSearchTerm] = useState('')
  const [backorderStatusFilter, setBackorderStatusFilter] = useState('hide') // 'all', 'partial', 'full', 'hide'

  const [branchFilter, setBranchFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Sorting state with localStorage persistence
  const [sortPreferences, setSortPreferences] = useLocalStorage('enquiriesSort', {
    field: 'created',
    direction: 'asc'
  })
  const sortField = sortPreferences.field
  const sortDirection = sortPreferences.direction

  const setSortField = (field) => setSortPreferences((prev) => ({ ...prev, field }))
  const setSortDirection = (direction) => setSortPreferences((prev) => ({ ...prev, direction }))

  // Collapsible sections state
  const [isSearchOpen, setIsSearchOpen] = useState(true)
  const [isFilterOpen, setIsFilterOpen] = useState(true)
  const [isSortOpen, setIsSortOpen] = useState(true)

  const [statusFilters, setStatusFilters] = useState(defaultStatusFilters)

  // response format:
  // {
  //   enquiries: mainEnquiries,
  //   priorityEnquiries: priorityEnquiries,
  //   metadata: {
  //     cacheHit: mainCacheHit && historicalCacheHit,
  //     mainCacheHit,
  //     historicalCacheHit,
  //     totalEnquiries: mainEnquiries.length,
  //     totalPriority: priorityEnquiries.length,
  //     fetchedAt: new Date().toISOString(),
  //     durationMs: duration
  //   }
  // }
  const { isLoading, isFetching, data, refetch } = useApi({
    url: `${apir}/connectplus/enquiries/merged`,
    method: 'post',
    data: { StartDate: startDate, EndDate: endDate },
    refetch: 30 * 1000,
    initial: { enquiries: [], priorityEnquiries: [], metadata: {} }
  })

  // Update status when loading enquiries
  useEffect(() => {
    if (isLoading) {
      setDataStatus('fetching_enquiries', 'Loading enquiries...')
    } else if (data.enquiries.length > 0) {
      setDataStatus('ready', 'Data ready')
    }
  }, [isLoading, data.enquiries.length, setDataStatus])

  // Reset status when component unmounts
  useEffect(() => {
    return () => {
      resetDataStatus()
    }
  }, [resetDataStatus])

  if (isLoading) return null

  const toggleStatusFilter = (status) => {
    setStatusFilters((prev) => ({ ...prev, [status]: !prev[status] }))
  }

  const branches = [
    ...new Set([...data.enquiries, ...data.priorityEnquiries].map((enquiry) => enquiry.Branch))
  ]

  const getStatusCounts = () => {
    const statusCounts = {
      'FactorUnread': 0,
      'Garage Confirmed': 0,
      'Waiting': 0,
      'Ordered': 0,
      'Order': 0,
      'Quoted': 0,
      'Cancelled': 0,
      'PartialBackorder': 0,
      'FullBackorder': 0
    }

    // Count from ALL enquiries (both priority and non-priority)
    const allEnquiries = [...data.enquiries, ...data.priorityEnquiries]

    // Filter by branch/search (but NOT status filters) for status counts
    const enquiriesForStatusCounting = allEnquiries.filter((enquiry) => {
      const matchesBranch =
        !branchFilter || enquiry.Branch?.toLowerCase() === branchFilter.toLowerCase()

      const customerName = enquiry.GarageName || ''
      const vehicleReg = enquiry.Reg || ''
      const parts = enquiry.Parts || ''
      const notes = enquiry.Notes || ''
      const garageAcctNo = enquiry.CustomerKeyCode || ''
      const garageName = enquiry.GarageName || ''

      const matchesSearch =
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicleReg.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parts.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notes.toLowerCase().includes(searchTerm.toLowerCase()) ||
        garageAcctNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        garageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.Status?.toLowerCase().includes(searchTerm.toLowerCase())

      return matchesBranch && matchesSearch
    })

    // Count statuses and backorders from filtered enquiries
    for (const enquiry of enquiriesForStatusCounting) {
      // Count statuses
      if ((enquiry.FactorUnread || 0) > 0) statusCounts.FactorUnread++
      if (enquiry.Status === 'Garage Confirmed') statusCounts['Garage Confirmed']++
      if (enquiry.Status === 'Waiting') statusCounts.Waiting++
      if (enquiry.Status === 'Ordered') statusCounts.Ordered++
      if (enquiry.Status === 'Order') statusCounts.Order++
      if (enquiry.Status === 'Quoted') statusCounts.Quoted++
      if (enquiry.Status === 'Cancelled') statusCounts.Cancelled++

      // Count backorders using pre-calculated backorderType from backend
      const backorderType = enquiry.backorderType || 'none'
      if (backorderType === 'partial') {
        statusCounts.PartialBackorder++
      } else if (backorderType === 'full') {
        statusCounts.FullBackorder++
      }
    }

    return statusCounts
  }

  const statusCounts = getStatusCounts()

  // Apply backorder filter and auto-adjust status filters
  const applyBackorderFilter = (backorderType) => {
    // Find statuses that have this backorder type across ALL enquiries
    const statusesWithType = new Set()
    const allEnquiries = [...data.enquiries, ...data.priorityEnquiries]

    allEnquiries.forEach((enquiry) => {
      if (enquiry.backorderType === backorderType) {
        // Add unread status if applicable
        if ((enquiry.FactorUnread || 0) > 0) {
          statusesWithType.add('FactorUnread')
        }
        // Add enquiry status if it exists in our filters
        if (enquiry.Status && defaultStatusFilters.hasOwnProperty(enquiry.Status)) {
          statusesWithType.add(enquiry.Status)
        }
      }
    })

    // Reset status filters: turn off all, then enable only relevant ones
    const newStatusFilters = Object.keys(defaultStatusFilters).reduce((acc, key) => {
      acc[key] = statusesWithType.has(key)
      return acc
    }, {})

    setStatusFilters(newStatusFilters)
    setBackorderStatusFilter(backorderType)
  }

  // Filter function to apply to both main and priority enquiries
  const matchesFilters = (enquiry) => {
    const matchesBranch =
      !branchFilter || enquiry.Branch?.toLowerCase() === branchFilter.toLowerCase()
    const matchesStatus =
      !statusFilter || enquiry.Status?.toLowerCase() === statusFilter.toLowerCase()

    const customerName = enquiry.GarageName || ''
    const vehicleReg = enquiry.Reg || ''
    const parts = enquiry.Parts || ''
    const notes = enquiry.Notes || ''
    const garageAcctNo = enquiry.CustomerKeyCode || ''
    const garageName = enquiry.GarageName || ''

    const matchesSearch =
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicleReg.toLowerCase().includes(searchTerm.toLowerCase()) ||
      parts.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notes.toLowerCase().includes(searchTerm.toLowerCase()) ||
      garageAcctNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      garageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.Status?.toLowerCase().includes(searchTerm.toLowerCase())

    // Status filters: if none are checked, show all; if some are checked, only show matching ones
    const hasAnyStatusFilter = Object.values(statusFilters).some((v) => v)
    const matchesStatusFilters =
      !hasAnyStatusFilter ||
      (statusFilters.FactorUnread && (enquiry.FactorUnread || 0) > 0) ||
      (statusFilters['Garage Confirmed'] && enquiry.Status === 'Garage Confirmed') ||
      (statusFilters.Waiting && enquiry.Status === 'Waiting') ||
      (statusFilters.Ordered && enquiry.Status === 'Ordered') ||
      (statusFilters.Order && enquiry.Status === 'Order') ||
      (statusFilters.Quoted && enquiry.Status === 'Quoted') ||
      (statusFilters.Cancelled && enquiry.Status === 'Cancelled')

    // Check backorder status filter (using pre-calculated backorderType from backend)
    // Enquiries with unread messages always bypass backorder filter
    const hasUnreadMessage = (enquiry.FactorUnread || 0) > 0
    const backorderType = enquiry.backorderType || 'none'
    let matchesBackorderFilter = true

    if (backorderStatusFilter === 'all' || hasUnreadMessage) {
      matchesBackorderFilter = true
    } else if (backorderStatusFilter === 'hide') {
      matchesBackorderFilter = backorderType === 'none'
    } else if (backorderStatusFilter === 'partial') {
      matchesBackorderFilter = backorderType === 'partial'
    } else if (backorderStatusFilter === 'full') {
      matchesBackorderFilter = backorderType === 'full'
    }

    return (
      matchesBranch &&
      matchesStatus &&
      matchesSearch &&
      matchesStatusFilters &&
      matchesBackorderFilter
    )
  }

  // Apply filters to both arrays from backend (already separated by backend)
  const filteredMainEnquiries = data.enquiries.filter(matchesFilters)
  const filteredPriorityEnquiries = data.priorityEnquiries.filter(matchesFilters)

  // Sort both arrays using the same sorting preferences
  const priorityEnquiries = sortEnquiries([...filteredPriorityEnquiries], sortField, sortDirection)
  const otherEnquiries = sortEnquiries([...filteredMainEnquiries], sortField, sortDirection)

  const refetchAll = () => {
    refetch()
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        position: 'relative'
      }}
    >
      {/* Fixed Left Sidebar */}
      <Box
        sx={{
          width: '25%',
          minWidth: '300px',
          position: 'sticky',
          top: 0,
          left: 0,
          height: '100vh',
          overflowY: 'auto',
          backgroundColor: 'background.default',
          padding: 3,
          zIndex: 1,
          alignSelf: 'flex-start'
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ lineHeight: 1 }}>
                  Search
                </Typography>
                {!isSearchOpen && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden'
                    }}
                  >
                    {`(`}
                    {searchTerm ? `filter: "${searchTerm}"; ` : ''}
                    {`branch: ${branchFilter || 'All'}; `}
                    {`dates: ${startDate} → ${endDate}`}
                    {`)`}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => setIsSearchOpen((v) => !v)}
                aria-label="toggle search section"
              >
                {isSearchOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Grid>
          {isSearchOpen && (
            <>
              <Grid item xs={12}>
                <TextField
                  sx={{ width: '100%' }}
                  label="Type to search..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  sx={{ width: '100%' }}
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  sx={{ width: '100%' }}
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel id="branch-label">Branch</InputLabel>
                  <Select
                    labelId="branch-label"
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    label="Branch"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch} value={branch}>
                        {branch}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ paddingTop: 2 }}>
          <Box
            sx={{
              paddingBottom: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6">Filter by status</Typography>
            <IconButton
              size="small"
              onClick={() => setIsFilterOpen((v) => !v)}
              aria-label="toggle filter section"
            >
              {isFilterOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {isFilterOpen && (
            <>
              {Object.entries(statusFilters).map(([status, isActive]) => (
                <Box key={status} sx={{ paddingBottom: 2 }}>
                  <Badge
                    badgeContent={statusCounts[status]}
                    sx={{ '& .MuiBadge-badge': { backgroundColor: '#3498DB', color: 'white' } }}
                  >
                    <Button
                      variant="outlined"
                      sx={{
                        'backgroundColor': isActive ? '#FFD700' : 'transparent',
                        'color': isActive ? '#000000' : 'white',
                        'borderColor': isActive ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                        'borderWidth': '1px',
                        'borderRadius': '12px',
                        'fontWeight': '600',
                        'textTransform': 'uppercase',
                        'boxShadow': isActive
                          ? '0 2px 8px rgba(255, 215, 0, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          backgroundColor: isActive ? '#FFD700' : 'rgba(255, 107, 53, 0.1)',
                          borderColor: '#FFD700',
                          color: isActive ? '#000000' : 'white'
                        }
                      }}
                      onClick={() => toggleStatusFilter(status)}
                    >
                      {statusLabels[status]}
                    </Button>
                  </Badge>
                </Box>
              ))}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
                  Backorder Status
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant={backorderStatusFilter === 'all' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      // Reset to 'all' - show all enquiries regardless of backorder status
                      setBackorderStatusFilter('all')
                      // Restore default status filters when going back to 'all'
                      setStatusFilters({ ...defaultStatusFilters })
                    }}
                    sx={{
                      'color': backorderStatusFilter === 'all' ? 'white' : '#3498DB',
                      'borderColor': '#3498DB',
                      'backgroundColor':
                        backorderStatusFilter === 'all' ? '#3498DB' : 'transparent',
                      '&:hover': {
                        borderColor: '#2980B9',
                        backgroundColor:
                          backorderStatusFilter === 'all' ? '#2980B9' : 'rgba(52, 152, 219, 0.1)'
                      }
                    }}
                  >
                    All
                  </Button>
                  <Button
                    variant={backorderStatusFilter === 'hide' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      // Toggle hide - hide enquiries with any backorder (partial or full)
                      if (backorderStatusFilter === 'hide') {
                        setBackorderStatusFilter('all')
                        setStatusFilters({ ...defaultStatusFilters })
                      } else {
                        setBackorderStatusFilter('hide')
                        // Don't change status filters when hiding - just hide backordered items
                      }
                    }}
                    sx={{
                      'color': backorderStatusFilter === 'hide' ? 'white' : '#E74C3C',
                      'borderColor': '#E74C3C',
                      'backgroundColor':
                        backorderStatusFilter === 'hide' ? '#E74C3C' : 'transparent',
                      'opacity': backorderStatusFilter === 'hide' ? 1 : 0.8,
                      '&:hover': {
                        borderColor: '#C0392B',
                        backgroundColor:
                          backorderStatusFilter === 'hide' ? '#C0392B' : 'rgba(231, 76, 60, 0.1)'
                      }
                    }}
                  >
                    Hide Backordered
                  </Button>
                  <Button
                    variant={backorderStatusFilter === 'partial' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => applyBackorderFilter('partial')}
                    sx={{
                      'color': backorderStatusFilter === 'partial' ? 'white' : '#F39C12',
                      'borderColor': '#F39C12',
                      'backgroundColor':
                        backorderStatusFilter === 'partial' ? '#F39C12' : 'transparent',
                      '&:hover': {
                        borderColor: '#E67E22',
                        backgroundColor:
                          backorderStatusFilter === 'partial'
                            ? '#E67E22'
                            : 'rgba(243, 156, 18, 0.1)'
                      }
                    }}
                  >
                    Partial ({statusCounts.PartialBackorder})
                  </Button>
                  <Button
                    variant={backorderStatusFilter === 'full' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => applyBackorderFilter('full')}
                    sx={{
                      'color': backorderStatusFilter === 'full' ? 'white' : '#9B59B6',
                      'borderColor': '#9B59B6',
                      'backgroundColor':
                        backorderStatusFilter === 'full' ? '#9B59B6' : 'transparent',
                      '&:hover': {
                        borderColor: '#8E44AD',
                        backgroundColor:
                          backorderStatusFilter === 'full' ? '#8E44AD' : 'rgba(155, 89, 182, 0.1)'
                      }
                    }}
                  >
                    Full Backorder ({statusCounts.FullBackorder})
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ paddingTop: 2 }}>
          <Box
            sx={{
              paddingBottom: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>
              Sort by
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsSortOpen((v) => !v)}
              aria-label="toggle sort section"
            >
              {isSortOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {isSortOpen && (
            <Grid container spacing={1}>
              {[
                { key: 'document', label: 'Doc No' },
                { key: 'acctNo', label: 'Acct No' },
                { key: 'acctName', label: 'Acct Name' },
                { key: 'reg', label: 'Reg' },
                { key: 'status', label: 'Status' },
                { key: 'created', label: 'Created Date' },
                { key: 'modified', label: 'Modified Date' }
              ].map(({ key, label }) => {
                const isActive = sortField === key
                const Icon = sortDirection === 'asc' ? ArrowUpward : ArrowDownward
                return (
                  <Grid item xs={12} key={key}>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        'justifyContent': 'space-between',
                        'backgroundColor': isActive ? '#3498DB' : 'transparent',
                        'color': 'white',
                        'borderColor': isActive ? '#3498DB' : 'rgba(255, 255, 255, 0.3)',
                        'borderWidth': '1px',
                        'borderRadius': '12px',
                        'fontWeight': '600',
                        'textTransform': 'uppercase',
                        'boxShadow': isActive
                          ? '0 2px 8px rgba(52, 152, 219, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          backgroundColor: isActive ? '#3498DB' : 'rgba(52, 152, 219, 0.1)',
                          borderColor: '#3498DB',
                          color: 'white'
                        }
                      }}
                      endIcon={isActive ? <Icon /> : null}
                      onClick={() => {
                        if (isActive) {
                          setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
                        } else {
                          setSortField(key)
                        }
                      }}
                    >
                      {label}
                    </Button>
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          minWidth: 'calc(75% - 24px)',
          maxHeight: '100vh',
          overflowY: 'auto',
          padding: 3,
          position: 'relative',
          zIndex: 0
        }}
      >
        <Grid container spacing={2} sx={{ paddingRight: 1 }}>
          <Grid item xs={24}>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              Priority enquiries
            </Typography>
            {priorityEnquiries.length > 0 ? (
              priorityEnquiries.map((enquiry, index) => (
                <EnquiryItem
                  key={enquiry.EnquiryId}
                  enquiry={enquiry}
                  locked={enquiry.Locked}
                  lockedBy={enquiry.LockedUsername}
                  currentUser={username}
                  isHighlighted={true}
                  backorderStatus={enquiry.backorderStatus}
                  refetch={() => {
                    refetchAll()
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ py: 2 }}>
                No enquiries to display.
              </Typography>
            )}
          </Grid>
          <Grid item xs={24}>
            <Typography variant="h6" sx={{ lineHeight: 1, mb: 2 }}>
              Other enquiries
            </Typography>
            {otherEnquiries.length > 0 ? (
              otherEnquiries.map((enquiry, index) => (
                <EnquiryItem
                  key={enquiry.EnquiryId}
                  enquiry={enquiry}
                  locked={enquiry.Locked}
                  lockedBy={enquiry.LockedUsername}
                  backorderStatus={enquiry.backorderStatus}
                  currentUser={username}
                  refetch={() => {
                    refetchAll()
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ py: 2 }}>
                No enquiries to display.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default Enquiries
